import { lookupWordBand } from '../data/wordBands';

/**
 * 词形还原（lemmatization）。
 *
 * 为什么需要它：阅读页点击到的是**文中形式**（`studies`、`running`、`don't`），
 * 而词典按**原形**收录（`study`、`run`、`do`）。不还原就会出现"点得进去查不到"。
 *
 * 实现取「不规则表 + 后缀规则 + 候选列表」三段式，而**不是**单一结果：
 * - 英文屈折有大量歧义（`uses` 可能是 `use` 也可能是 `us`；`lives` 可能是 `life` 也可能是 `live`），
 *   单点猜测必然有错。因此这里返回**有序候选列表**，由调用方（词典服务）
 *   按顺序去 D1/KV/上游词典里探测，**第一个真正命中的候选**才算数。
 * - 不上真正的词性标注与形态分析器：Workers 运行时不该塞进几十 MB 的 NLP 模型，
 *   而上游词典本身就是最好的裁判 —— 候选探测的代价只是一次 KV 读。
 *
 * 调用方约定：`lemmaCandidates()[0]` 只是"最可能的原形"，用于展示与建索引；
 * **判定原形必须走候选探测**（见 apps/api 的 dictionary.service）。
 *
 * `text.ts` 的 tokenize 依赖本文件，因此这里保持零依赖、纯函数。
 */

// ---------------------------------------------------------------------------
// 不规则形式表
// ---------------------------------------------------------------------------

/**
 * 不规则动词与不规则复数。
 *
 * 打包成 `form:base` 的字符串常量而不是对象字面量，原因有两个：
 * 1. 400+ 条映射写成字面量会有 400 行，淹没真正需要读的逻辑；
 * 2. 打包后每一行都是一组同源变化（be 的各种形式在一行），反而更好审查。
 *
 * 只收**不规则**形式（规则变化交给下面的后缀规则），并且只收高频词 ——
 * 收录标准是"大学英语三课文中真会碰到"。
 */
const IRREGULAR_PAIRS = `
am:be is:be are:be was:be were:be been:be being:be
has:have had:have having:have does:do did:do done:do doing:do
goes:go went:go gone:go says:say said:say got:get gotten:get made:make
took:take taken:take came:come saw:see seen:see found:find gave:give given:give
told:tell thought:think knew:know known:know became:become left:leave felt:feel
brought:bring began:begin begun:begin kept:keep held:hold wrote:write written:write
stood:stand heard:hear meant:mean met:meet ran:run paid:pay sat:sit
spoke:speak spoken:speak led:lead grew:grow grown:grow lost:lose fell:fall fallen:fall
sent:send built:build understood:understand drew:draw drawn:draw broke:break broken:break
spent:spend rose:rise risen:rise drove:drive driven:drive bought:buy wore:wear worn:wear
chose:choose chosen:choose ate:eat eaten:eat sold:sell sang:sing sung:sing slept:sleep
taught:teach flew:fly flown:fly caught:catch threw:throw thrown:throw sought:seek
fought:fight hid:hide hidden:hide struck:strike dealt:deal forgot:forget forgotten:forget
forgave:forgive forgiven:forgive froze:freeze frozen:freeze rode:ride ridden:ride
rang:ring rung:ring shook:shake shaken:shake shot:shoot showed:show shown:show
shut:shut spread:spread stole:steal stolen:steal stuck:stick swept:sweep swam:swim swum:swim
tore:tear torn:tear woke:wake woken:wake won:win bit:bite bitten:bite blew:blow blown:blow
burnt:burn cast:cast cost:cost crept:creep dug:dig hung:hang hurt:hurt lent:lend
lay:lie lain:lie lit:light quit:quit set:set shone:shine sank:sink sunk:sink
slid:slide spun:spin split:split spoilt:spoil stung:sting swore:swear sworn:swear
swung:swing wound:wind withdrew:withdraw withdrawn:withdraw arose:arise arisen:arise
awoke:awake awoken:awake bore:bear borne:bear beat:beat beaten:beat bound:bind
bled:bleed bred:breed clung:cling fed:feed fled:flee flung:fling forbade:forbid forbidden:forbid
ground:grind knelt:kneel laid:lay leapt:leap mistook:mistake mistaken:mistake
overcame:overcome rebuilt:rebuild repaid:repay strove:strive striven:strive
upset:upset wept:weep
children:child men:man women:woman feet:foot teeth:tooth geese:goose mice:mouse oxen:ox
indices:index analyses:analysis criteria:criterion phenomena:phenomenon theses:thesis
crises:crisis hypotheses:hypothesis matrices:matrix vertices:vertex
`;

const IRREGULAR_FORMS: ReadonlyMap<string, string> = new Map(
  IRREGULAR_PAIRS.trim()
    .split(/\s+/)
    .map((pair) => pair.split(':') as [string, string])
    .filter((pair) => pair.length === 2 && pair[0].length > 0 && pair[1].length > 0),
);

/**
 * 缩写形式。只收「切掉后缀得不到正确原形」的几个 ——
 * 其余（`it's` / `we're` / `I've` / `they'll`）用通用后缀规则处理即可。
 */
const CONTRACTIONS: ReadonlyMap<string, string> = new Map([
  ["can't", 'can'],
  ["won't", 'will'],
  ["shan't", 'shall'],
  ["ain't", 'be'],
  ["let's", 'let'],
]);

/** 会吞掉词尾 'e' 的后缀（`make` + `ing` → `making`）。 */
const CONTRACTION_SUFFIXES = ["n't", "'s", "'re", "'ve", "'ll", "'d", "'m", "'em"] as const;

/** 需要"还原双写辅音"的情形：`running` → `runn` → `run`。 */
const DOUBLED_TAIL = /([bdfglmnprt])\1$/;

/**
 * `-ing` / `-ed` 的词干到底有没有吞掉一个静音 `e`？
 *
 * 这是屈折还原里最要命的一处歧义：`making` 的词干是 `mak`（对应 `make`），
 * 而 `walking` 的词干就是 `walk`（`walke` 不是词）。纯规则无法可靠区分
 * （`k` 结尾两者都常见：make / take / like ←→ walk / talk / think）。
 *
 * 因此这里**用内置词表当裁判**：优先选词表里存在的那个形式。
 * 词表是随包分发的静态数据（约 2300 个高频词），查表是纯内存操作、可离线、可复现。
 * 两边都查不到时（生僻词）才退回正字法判断：
 * 英语词尾几乎不可能出现 `v` / `u` / `z` / `c`（`love` `continue` `realize` `produce`），
 * 单个 `s` 结尾也基本是吞了 `e`（`raise` `close` `use`，而 `pass` 是双写 `ss`）。
 *
 * 注意这只是**候选排序**：真正判定原形时调用方会把所有候选逐个拿去探测，
 * 所以这里判错最多多一次查表，不会得到错误结果。
 */
function prefersSilentE(stem: string): boolean {
  const withE = lookupWordBand(`${stem}e`) !== null;
  const bare = lookupWordBand(stem) !== null;
  if (withE && !bare) return true;
  if (bare && !withE) return false;
  return /[vuzc]$/.test(stem) || (stem.endsWith('s') && !stem.endsWith('ss'));
}

/** 双写辅音结尾时把去双写形式排在前面，否则按 prefersSilentE 决定 `+e` 与光词干的顺序。 */
function pushStemForms(out: string[], stem: string): void {
  if (DOUBLED_TAIL.test(stem)) {
    out.push(stem.slice(0, -1), stem);
    return;
  }
  if (prefersSilentE(stem)) out.push(`${stem}e`, stem);
  else out.push(stem, `${stem}e`);
}

function stripContraction(word: string): string | null {
  for (const suffix of CONTRACTION_SUFFIXES) {
    if (word.endsWith(suffix) && word.length > suffix.length) {
      const base = word.slice(0, word.length - suffix.length);
      if (base.length > 0) return base;
    }
  }
  // 所有格复数（students'）与截断式（rock 'n' roll 的尾撇号）
  if (word.endsWith("'") && word.length > 1) return word.slice(0, -1);
  return null;
}

// ---------------------------------------------------------------------------
// 后缀规则
// ---------------------------------------------------------------------------

/**
 * 按后缀产生候选原形，**顺序即优先级**。
 *
 * 每条规则都宁可多给候选也不漏：候选是拿去探测的，多一个候选只多一次查表，
 * 而漏掉候选会让用户点了词却查不到东西 —— 后者是可见的功能缺陷。
 */
function ruleCandidates(word: string): string[] {
  const out: string[] = [];

  // studies → study / studie（movies 属于后者）
  if (word.length > 4 && word.endsWith('ies')) {
    const stem = word.slice(0, -3);
    out.push(`${stem}y`, `${stem}ie`);
  }

  // lives → life / lif / live（f→ves 的变化，含 halves / wolves / knives）
  if (word.length > 4 && word.endsWith('ves')) {
    const stem = word.slice(0, -3);
    out.push(`${stem}fe`, `${stem}f`);
  }

  // boxes → box；uses → us / use（两条都给，交给探测决定）
  if (word.length > 3 && word.endsWith('es')) {
    out.push(word.slice(0, -2), word.slice(0, -1));
  }

  // books → book；跳过 -ss / -us / -is 结尾（class / bus / analysis 本身是原形）
  if (word.length > 3 && word.endsWith('s') && !/(ss|us|is)$/.test(word)) {
    out.push(word.slice(0, -1));
  }

  if (word.length > 4 && word.endsWith('ing')) {
    pushStemForms(out, word.slice(0, -3)); // running → run；making → make；walking → walk
  }

  if (word.length > 3 && word.endsWith('ed')) {
    if (word.length > 4 && word.endsWith('ied')) out.push(`${word.slice(0, -3)}y`); // studied → study
    pushStemForms(out, word.slice(0, -2)); // stopped → stop；moved → move；walked → walk
  }

  // 比较级/最高级：只处理足够长的词，避免把 water / number 这类词拆坏
  if (word.length > 5 && word.endsWith('er')) {
    pushStemForms(out, word.slice(0, -2)); // bigger → big；larger → large
  }
  if (word.length > 6 && word.endsWith('est')) {
    pushStemForms(out, word.slice(0, -3)); // biggest → big；largest → large
  }

  return out;
}

// ---------------------------------------------------------------------------
// 对外接口
// ---------------------------------------------------------------------------

/**
 * 返回一个词的可能原形候选，**有序、去重**，最后一项一定是输入本身。
 *
 * 例：
 *   lemmaCandidates('Studies')  → ['study', 'studie', 'studies']
 *   lemmaCandidates("don't")    → ['do', "don't"]
 *   lemmaCandidates('well-known') → ['well-known', 'well', 'known', ...]
 *   lemmaCandidates('')         → []
 */
export function lemmaCandidates(surface: string): string[] {
  const word = surface.trim().toLowerCase().replace(/’/g, "'");
  if (word.length === 0) return [];

  const out: string[] = [];
  const seen = new Set<string>();

  const push = (candidate: string | null | undefined): void => {
    if (!candidate) return;
    const value = candidate.replace(/^['-]+|['-]+$/g, '');
    if (value.length === 0 || value.length > 48) return;
    if (seen.has(value)) return;
    seen.add(value);
    out.push(value);
  };

  // 连字符词：整体优先（词典里 well-known 就是词条），再逐段还原
  if (word.includes('-')) {
    push(word);
    for (const part of word.split('-')) {
      for (const candidate of lemmaCandidates(part)) push(candidate);
    }
    return out;
  }

  // 1) 缩写：先得到"被缩写的那个词"，再看它是否还需要查不规则表
  //    （doesn't → does → do 要走两步，所以这里不能只 push 一次）
  let base = word;
  const contraction = CONTRACTIONS.get(word);
  if (contraction !== undefined) {
    base = contraction;
  } else {
    const stripped = stripContraction(word);
    if (stripped !== null) base = stripped;
  }

  // 2) 不规则表
  const irregular = IRREGULAR_FORMS.get(base);
  if (irregular !== undefined) push(irregular);
  // 缩写/所有格：去掉后缀后的那个词本身也可能是原形（we're → we）
  if (base !== word) push(base);

  // 3) 后缀规则
  for (const candidate of ruleCandidates(base)) push(candidate);

  // 4) 兜底：文中形式本身也可能是词条（building / reading / works）
  push(word);

  return out;
}

/** 便捷版：只取最可能的原形（等价于 `lemmaCandidates(surface)[0]`）。 */
export function lemmaOf(surface: string): string {
  return lemmaCandidates(surface)[0] ?? surface.trim().toLowerCase();
}

/** 该形式是否命中不规则表（诊断与测试用，业务代码不需要）。 */
export function isIrregularForm(surface: string): boolean {
  return IRREGULAR_FORMS.has(surface.trim().toLowerCase().replace(/’/g, "'"));
}
